import { inject, InjectionToken } from '@angular/core';
import {
  AUTH_CODE_VERIFIER_STORAGE_KEY,
  AUTH_REDIRECT_URL_STORAGE_KEY,
  AUTH_TOKEN_STORAGE_KEY,
  AUTH_USER_AUTHORITIES_STORAGE_KEY,
  AUTH_USER_NAME_STORAGE_KEY,
  Authorities,
  AuthStep,
  IpensiiAuthConfig,
  IpEnvironment,
} from '@ipensii/shared/domain-shared';
import { ArrayUtils, EncodeUtils, ObjectUtils } from '@ipensii/shared/util-shared';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { CacheService } from './cache.service';
import { EnvironmentService } from '@irian-ro/util-environment';
import { jwtDecode } from 'jwt-decode';

interface TokenResponse {
  access_token: string;
  expires_in: number;
  id_token: string;
  scope: string;
  token_type: string;
}

interface UserInfo {
  sub: string;
  email: string;
  authorities: Array<Authorities>;
}

export const AUTH_CONFIG = new InjectionToken<IpensiiAuthConfig>('AUTH_CONFIG');

export class AuthService {
  private readonly config = inject(AUTH_CONFIG);
  private readonly http = inject(HttpClient);
  private readonly cacheService = inject(CacheService);
  private readonly env: EnvironmentService<IpEnvironment> = inject(EnvironmentService);

  private static get codeVerifier(): string {
    const array = new Uint32Array(56 / 2);
    crypto.getRandomValues(array);
    return Array.from(array, AuthService.dec2hex).join('');
  }

  get isLoggedIn(): boolean {
    return !!this.authToken;
  }

  get hasEmail(): boolean {
    if(this.authToken){
      const decodedToken : UserInfo = jwtDecode(this.authToken);
      return !!decodedToken.email
    }
    return false;
  }

  get isLoggedInAndHasEmail(): boolean {
    return this.isLoggedIn && this.hasEmail;
  }

  get authToken(): string | null {
    return this.cacheService.getItem(AUTH_TOKEN_STORAGE_KEY);
  }

  set authToken(token: string) {
    this.cacheService.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    this.user = jwtDecode(token);
  }

  set user(user: UserInfo) {
    if (user) {
      this.cacheService.setItem(AUTH_USER_NAME_STORAGE_KEY, user.sub);
      this.cacheService.setItem(AUTH_USER_AUTHORITIES_STORAGE_KEY, user.authorities.join());
    } else {
      this.cacheService.removeItem(AUTH_USER_NAME_STORAGE_KEY);
      this.cacheService.removeItem(AUTH_USER_AUTHORITIES_STORAGE_KEY);
    }
  }

  get authUser(): string | null {
    return this.cacheService.getItem(AUTH_USER_NAME_STORAGE_KEY);
  }

  get authUserAuthorities(): Array<Authorities> {
    const authorities = this.cacheService.getItem(AUTH_USER_AUTHORITIES_STORAGE_KEY);
    return authorities ? (authorities.split(',') as Array<Authorities>) : [];
  }

  get isAdmin(): boolean {
    return this.hasAnyAuthority(Authorities.ROLE_ADMIN, Authorities.ROLE_TECH_ADMIN);
  }

  get isHr(): boolean {
    return this.hasAnyAuthority(Authorities.ROLE_HR_ADMIN, Authorities.ROLE_HR);
  }

  get redirectUrl(): string {
    return this.cacheService.getItem(AUTH_REDIRECT_URL_STORAGE_KEY) || '';
  }

  private static dec2hex(dec: number) {
    return ('0' + dec.toString(16)).slice(-2);
  }

  private static sha256(plain: string): Promise<ArrayBuffer> {
    // returns promise ArrayBuffer
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return crypto.subtle.digest('SHA-256', data);
  }

  private static async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const hashed = await AuthService.sha256(codeVerifier);
    return EncodeUtils.encodeBase64(hashed);
  }

  hasAnyAuthority(...authorities: Array<Authorities>): boolean {
    return ArrayUtils.includesAny(this.authUserAuthorities, authorities);
  }

  async authorize(redirectUrl: string = location.href): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { authority, grant_type, ...configParams } = this.config;
    const code_verifier = AuthService.codeVerifier;
    const code_challenge = await AuthService.generateCodeChallenge(code_verifier);
    const params = { ...configParams, code_challenge };
    this.clearLocalStorage();
    this.cacheService.setItem(AUTH_CODE_VERIFIER_STORAGE_KEY, code_verifier);
    this.cacheService.setItem(AUTH_REDIRECT_URL_STORAGE_KEY, redirectUrl);
    location.href = `${this.env.getEnvironment().backendUrl}/${this.config.authority}/${
      AuthStep.authorize
    }?${ObjectUtils.toQueryParams(params)}`;
  }

  getToken(code: string): Observable<TokenResponse> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { authority, scope, response_type, code_challenge_method, ...configParams } = this.config;
    const code_verifier = this.cacheService.getItem(AUTH_CODE_VERIFIER_STORAGE_KEY);
    const params = { ...configParams, code, code_verifier };
    return this.http
      .post<TokenResponse>(
        `${this.env.getEnvironment().backendUrl}/${this.config.authority}/${AuthStep.token}?${ObjectUtils.toQueryParams(
          params
        )}`,
        {}
      )
      .pipe(
        tap((r) => (this.authToken = r.access_token)),
        tap(() => this.cacheService.removeItem(AUTH_CODE_VERIFIER_STORAGE_KEY))
      );
  }

  loggOut() {
    this.clearLocalStorage();
    location.href = '';
  }

  private clearLocalStorage() {
    this.cacheService.removeItem(AUTH_CODE_VERIFIER_STORAGE_KEY);
    this.cacheService.removeItem(AUTH_REDIRECT_URL_STORAGE_KEY);
    this.cacheService.removeItem(AUTH_USER_AUTHORITIES_STORAGE_KEY);
    this.cacheService.removeItem(AUTH_USER_NAME_STORAGE_KEY);
    this.cacheService.removeItem(AUTH_TOKEN_STORAGE_KEY);
  }
}
